/**
 * Demo mode.
 *
 * Visiting `/demo` switches the whole portal into a self-contained showcase:
 * a custom axios adapter is installed so every API call is answered from
 * `demoFixtures.ts` instead of hitting the backend, and a fake signed-in
 * session is seeded so the router guard lets you straight into the dashboard.
 *
 * There are no per-screen changes and no fake copy of the UI — the real
 * components render the canned data. The only persistent trace is a single
 * `demoMode` localStorage flag. While demo mode is active the sidenav swaps its
 * "Log out" action for "Exit demo mode" (see `exitDemoMode()` below), which
 * leaves any real session untouched.
 */
import axios from 'axios'
import type { AxiosAdapter, AxiosResponse } from 'axios'

import { useAuthStore } from '@/stores/authStore'
import { useOrganizationStore } from '@/stores/organizationStore'
import { DEMO_TOKEN, demoOrg, demoUser, resolveFixture } from './demoFixtures'

const DEMO_FLAG = 'demoMode'
const DEMO_PATH = '/demo'

/** True when entering via `/demo` or already inside a demo session. */
export function isDemoActive(): boolean {
  return localStorage.getItem(DEMO_FLAG) === 'true' || window.location.pathname === DEMO_PATH
}

/** axios adapter that answers every request from canned fixtures. */
const demoAdapter: AxiosAdapter = async (config) => {
  const method = (config.method ?? 'get').toLowerCase()
  const path = (config.url ?? '').replace(/^https?:\/\/[^/]+/, '').split('?')[0]

  // A little latency so loading spinners and skeletons still flash naturally.
  await new Promise((resolve) => setTimeout(resolve, 120 + Math.random() * 200))

  return {
    data: resolveFixture(method, path, config),
    status: 200,
    statusText: 'OK',
    headers: {},
    config,
  } as AxiosResponse
}

/**
 * Activate demo mode. Call this once at startup, before `app.mount()`.
 * Requires Pinia to be installed already.
 */
export function enableDemoMode(): void {
  localStorage.setItem(DEMO_FLAG, 'true')

  // Route every axios call through the fixtures — no real network traffic.
  axios.defaults.adapter = demoAdapter

  // Seed a signed-in, email-confirmed session so the router guard passes.
  const authStore = useAuthStore()
  authStore.$patch({ token: DEMO_TOKEN, user: demoUser })

  // Pre-seed the org so the dashboard renders immediately without a flicker.
  const orgStore = useOrganizationStore()
  orgStore.currentOrganization = demoOrg
  // The `/demo` route redirects to /dashboard/usage from here.
}

/**
 * Leave demo mode without logging anyone out. Clears the demo flag and the
 * demo org, then hard-reloads so the fixture adapter is dropped. A real user's
 * auth token is left intact, so they land back in their own session; a pure
 * demo visitor simply returns to the login page.
 */
export function exitDemoMode(): void {
  localStorage.removeItem(DEMO_FLAG)
  localStorage.removeItem('currentOrganization')
  window.location.assign('/')
}
